import React from 'react';
import type { StaffAction } from '../../types/registerReport';
import TreatsReportPanel from './TreatsReportPanel';
import {
  NO_STAFF_DATA, athensTime, euro, staffActionLabel, staffActionTotals, staffActionWhat,
} from './registerFormat';

/**
 * Καρτέλα «Κεράσματα & ακυρώσεις» του «Ταμείο» (μόνο καταστήματα με τραπέζια):
 *  - «Αναλυτικά»: κάθε κέρασμα, έκπτωση, ακύρωση και ακύρωση πληρωμής της ανοιχτής περιόδου, με ώρα
 *    Ελλάδας, τραπέζι, ποιος και γιατί — τα στοιχεία της αναφοράς ταμείου (`report.staffActions`), όπως
 *    τα τυπώνει η ενότητα «Κεράσματα & ακυρώσεις» του Ζ/Χ.
 *  - «Ανά είδος»: η αναφορά κερασμάτων (είδη/τεμάχια/αξία, και προηγούμενα Ζ) — πάντα, χωρίς τον
 *    παλιό διακόπτη treats_report_enabled (12/10/2026).
 */

export type StaffActionsView = 'list' | 'items';

interface Props {
  storeId: number;
  /** `report.staffActions` — λείπει σε παλιό backend/snapshot. */
  actions: StaffAction[] | undefined;
  view: StaffActionsView;
  onViewChange: (view: StaffActionsView) => void;
  refreshKey?: number;
}

const VIEWS: Array<[StaffActionsView, string]> = [['list', 'Αναλυτικά'], ['items', 'Ανά είδος']];

const StaffActionsList: React.FC<{ actions: StaffAction[] | undefined }> = ({ actions }) => {
  if (!Array.isArray(actions)) {
    return <p data-testid="register-staff-actions-empty" className="text-sm text-t3 py-6 text-center">{NO_STAFF_DATA}</p>;
  }
  if (actions.length === 0) {
    return <p data-testid="register-staff-actions-empty" className="text-sm text-t3 py-6 text-center">Καμία καταχώριση</p>;
  }
  const totals = staffActionTotals(actions);
  return (
    <div className="space-y-3">
      <div data-testid="register-staff-totals" className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {totals.map(tot => (
          <div
            key={tot.key}
            data-testid={`register-staff-total-${tot.key}`}
            className={`rounded-xl border px-3 py-2 ${tot.count > 0
              ? 'border-line-strong/60 bg-surface-2 dark:bg-surface-2/40'
              : 'border-line/60 bg-surface-1 opacity-70'}`}
          >
            <p className="text-xs font-semibold text-t3">{tot.label}</p>
            <p className="text-base font-bold text-t1 tabular-nums">
              {tot.count} · {euro(tot.value)}
            </p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-line-strong/50 bg-surface-2 dark:bg-surface-2/40">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="bg-surface-1 dark:bg-surface-2/60 text-left text-xs font-semibold text-t3 uppercase tracking-wider">
              <th scope="col" className="px-3 py-2 w-16">Ώρα</th>
              <th scope="col" className="px-3 py-2 w-20">Τραπέζι</th>
              <th scope="col" className="px-3 py-2">Τι</th>
              <th scope="col" className="px-3 py-2">Ποιος</th>
              <th scope="col" className="px-3 py-2">Λόγος</th>
              <th scope="col" className="px-3 py-2 w-24 text-right">Ποσό</th>
            </tr>
          </thead>
          <tbody>
            {actions.map((a, i) => {
              const what = staffActionWhat(a);
              return (
                <tr key={`${a.kind}-${a.at}-${i}`} data-testid="register-staff-action" className="border-t border-line-strong/30 align-top">
                  <td className="px-3 py-2 tabular-nums text-t3">{athensTime(a.at)}</td>
                  <td className="px-3 py-2 font-semibold text-t1">{a.table != null ? `Τ${a.table}` : '—'}</td>
                  <td className="px-3 py-2">
                    <span className="block font-medium text-t1">{staffActionLabel(a)}</span>
                    {what && <span className="block text-xs text-t2 break-words">{what}</span>}
                  </td>
                  <td className="px-3 py-2 text-t2 break-words">{a.who || '—'}</td>
                  <td className="px-3 py-2 text-t3 italic break-words">{a.reason || ''}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-t1">{euro(a.amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StaffActionsTab: React.FC<Props> = ({ storeId, actions, view, onViewChange, refreshKey }) => {
  return (
    <div data-testid="register-staff-actions" className="space-y-4">
      <div role="group" aria-label="Προβολή" className="inline-flex gap-1 rounded-xl bg-surface-2 dark:bg-surface-2/40 p-1">
        {VIEWS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-pressed={view === id}
            onClick={() => onViewChange(id)}
            className={`cashier-choice min-h-[44px] px-5 rounded-lg text-sm font-semibold transition-all touch-manipulation active:scale-[0.97] ${view === id
              ? 'bg-blue-600 text-white shadow'
              : 'text-t2 hover:bg-surface-3'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {view === 'items'
        ? <TreatsReportPanel storeId={storeId} refreshKey={refreshKey} />
        : <StaffActionsList actions={actions} />}
    </div>
  );
};

export default StaffActionsTab;
